import { computed } from 'vue';
import { useOrderPricing } from '@shared/composables/useOrderPricing';
import { MoneyUtils } from '../utils/MoneyUtils';
export function useCheckoutWithDeliveryZones(cartStoreData, projectId) {
    const orderPricing = useOrderPricing(projectId);
    // ✅ Formatiere Lieferadresse für API Call
    const formattedDeliveryAddress = computed(() => {
        const addr = cartStoreData.deliveryAddress;
        if (!addr.street || !addr.city || !addr.postalCode) {
            return null;
        }
        return `${addr.street.trim()}, ${addr.postalCode.trim()} ${addr.city.trim()}`;
    });
    // ✅ Prüfe ob alle Adressdaten vorhanden sind
    const hasCompleteDeliveryAddress = computed(() => {
        const addr = cartStoreData.deliveryAddress;
        return !!(addr.street?.trim() && addr.city?.trim() && addr.postalCode?.trim());
    });
    // ✅ Automatische Preisberechnung mit Lieferadresse
    const calculatePricingWithDeliveryAddress = async () => {
        console.log('🚀 [useCheckoutWithDeliveryZones] Starting pricing calculation with delivery address');
        // Standard Parameter
        const items = cartStoreData.items;
        const deliveryType = cartStoreData.deliveryType;
        // ✅ Erweitere mit Lieferadresse für Delivery Orders
        if (deliveryType === 'delivery' && formattedDeliveryAddress.value) {
            console.log('📍 [useCheckoutWithDeliveryZones] Using delivery address:', formattedDeliveryAddress.value);
            // Verwende die neue erweiterte Methode
            return await orderPricing.calculateOrderPricingWithAddress(items, deliveryType, formattedDeliveryAddress.value);
        }
        else {
            // Standard Preisberechnung ohne Adresse
            return await orderPricing.calculateOrderPricing(items, deliveryType);
        }
    };
    // ✅ Separate Lieferzonenvalidierung (ohne vollständige Preisberechnung)
    const validateCurrentDeliveryAddress = async () => {
        if (!formattedDeliveryAddress.value) {
            console.warn('⚠️ [useCheckoutWithDeliveryZones] No delivery address to validate');
            return {
                success: false,
                inZone: false,
                error: 'Keine Lieferadresse vorhanden'
            };
        }
        console.log('🔍 [useCheckoutWithDeliveryZones] Validating current delivery address:', formattedDeliveryAddress.value);
        return await orderPricing.checkDeliveryZone(formattedDeliveryAddress.value);
    };
    // ✅ Automatische Aktualisierung wenn sich Lieferadresse ändert
    const refreshPricingOnAddressChange = async () => {
        if (cartStoreData.deliveryType === 'delivery' && hasCompleteDeliveryAddress.value) {
            console.log('🔄 [useCheckoutWithDeliveryZones] Address changed, refreshing pricing...');
            await calculatePricingWithDeliveryAddress();
        }
    };
    // ✅ Delivery Zone Status aus aktueller Pricing Response
    const deliveryZoneStatus = computed(() => {
        const pricingData = orderPricing.pricingData;
        if (!pricingData?.data?.delivery?.zoneInfo) {
            return null;
        }
        return {
            inZone: pricingData.data.delivery.zoneInfo.inZone,
            zoneName: pricingData.data.delivery.zoneInfo.zoneName,
            zoneId: pricingData.data.delivery.zoneInfo.zoneId,
            isZoneBasedFee: pricingData.data.delivery.zoneInfo.isZoneBasedFee,
            coordinates: pricingData.data.delivery.zoneInfo.coordinates
        };
    });
    // ✅ Liefergebühr Status (mit Zone-Info falls verfügbar)
    const deliveryFeeInfo = computed(() => {
        const pricingData = orderPricing.pricingData;
        if (!pricingData?.data?.delivery) {
            return null;
        }
        const delivery = pricingData.data.delivery;
        const feeCent = delivery.feeCent || 0;
        const feeEuro = MoneyUtils.centToEuro(feeCent);
        return {
            feeCent,
            feeEuro,
            isFree: feeCent === 0,
            reason: delivery.reason,
            isEligibleForFree: delivery.isEligibleForFree,
            isZoneBased: delivery.zoneInfo?.isZoneBasedFee || false,
            zoneName: delivery.zoneInfo?.zoneName
        };
    });
    // ✅ Validation Summary für UI
    const checkoutValidation = computed(() => {
        const validation = {
            canProceed: true,
            errors: [],
            warnings: []
        };
        // Items check
        if (cartStoreData.items.length === 0) {
            validation.canProceed = false;
            validation.errors.push('Warenkorb ist leer');
        }
        // Delivery address check
        if (cartStoreData.deliveryType === 'delivery') {
            if (!hasCompleteDeliveryAddress.value) {
                validation.canProceed = false;
                validation.errors.push('Lieferadresse ist unvollständig');
            }
            else if (deliveryZoneStatus.value && !deliveryZoneStatus.value.inZone) {
                validation.canProceed = false;
                validation.errors.push('Lieferadresse liegt außerhalb des Liefergebiets');
            }
        }
        // Pricing check
        if (orderPricing.error) {
            validation.canProceed = false;
            validation.errors.push('Preisberechnung fehlgeschlagen');
        }
        // Minimum order check
        if (orderPricing.pricingData?.data?.validation?.minimumOrder &&
            !orderPricing.pricingData.data.validation.minimumOrder.isReached) {
            validation.canProceed = false;
            validation.errors.push(orderPricing.pricingData.data.validation.minimumOrder.message || 'Mindestbestellwert nicht erreicht');
        }
        // Restaurant status check
        if (orderPricing.pricingData?.data?.restaurantStatus &&
            !orderPricing.pricingData.data.restaurantStatus.isOpen) {
            validation.warnings.push(orderPricing.pricingData.data.restaurantStatus.message || 'Restaurant ist geschlossen');
        }
        return validation;
    });
    return {
        // Cart Store Data (passed as parameter)
        cartStoreData,
        // Order Pricing Integration
        orderPricing,
        // Delivery Address Utils
        formattedDeliveryAddress,
        hasCompleteDeliveryAddress,
        // Enhanced Methods
        calculatePricingWithDeliveryAddress,
        validateCurrentDeliveryAddress,
        refreshPricingOnAddressChange,
        // Computed Status
        deliveryZoneStatus,
        deliveryFeeInfo,
        checkoutValidation,
        // Shortcut Computed
        isInDeliveryZone: computed(() => deliveryZoneStatus.value?.inZone === true),
        hasZoneBasedDeliveryFee: computed(() => deliveryFeeInfo.value?.isZoneBased === true),
        canProceedToCheckout: computed(() => checkoutValidation.value.canProceed)
    };
}
